import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { NfcProgrammeDialog, NfcStatusBadge } from "../components/NfcTags";
import { nfcWriteSupported } from "../lib/nfc";
import { Broadcast, MagnifyingGlass, CheckCircle, WarningCircle } from "@phosphor-icons/react";

// Fast queue-based interface for programming a batch of asset tags. The same write/verify
// dialog is reused so there is no duplicated NFC logic. The operator must still confirm the
// asset/tag before each write.
export default function NfcBulkPage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("nfc_manage", "asset_manager", "administrator", "super_admin");
  const [assets, setAssets] = useState([]);
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState("");
  const [prog, setProg] = useState(null);
  const supported = nfcWriteSupported();

  const load = () => {
    api.get("/assets").then((r) => setAssets(r.data.filter((a) => !a.archived))).catch(() => {});
    api.get("/tags?target_type=asset").then((r) => setTags(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const activeTagByAsset = useMemo(() => {
    const m = {};
    tags.forEach((t) => { if (t.is_active) (m[t.target_id] = m[t.target_id] || []).push(t); });
    return m;
  }, [tags]);

  const queue = useMemo(() => {
    const list = assets.filter((a) => {
      if (!q) return true;
      const b = `${a.asset_number} ${a.name} ${a.registration || ""}`.toLowerCase();
      return b.includes(q.toLowerCase());
    });
    return list.sort((a, b) => (parseInt(a.asset_number) || 0) - (parseInt(b.asset_number) || 0));
  }, [assets, q]);

  const startFor = async (asset) => {
    try {
      // Reuse an existing assigned/written tag for this asset if present, else issue a new one.
      const existing = (activeTagByAsset[asset.id] || []).find((t) => t.status === "assigned" || t.status === "written");
      let tag;
      if (existing) { tag = (await api.get(`/tags/${existing.id}`)).data; }
      else { tag = (await api.post("/tags", { target_type: "asset", target_id: asset.id, media: "nfc", position: "" })).data; }
      setProg(tag);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
  };

  const nextAfter = () => { setProg(null); load(); };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2"><Broadcast size={28} weight="bold" />Programme NFC Tags</h1>
        <p className="font-body text-sm text-slate-600">Work through assets and programme a tag for each. Confirm the asset before every write.</p>
      </div>

      {!supported && (
        <div data-testid="bulk-nfc-unsupported" className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 flex items-center gap-2">
          <WarningCircle size={22} weight="fill" className="text-amber-500" />
          <span className="font-body text-sm text-amber-800">NFC writing isn't supported on this device/browser. Open on an Android phone in Chrome to programme tags. You can still issue tags and print QR labels here.</span>
        </div>
      )}

      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="bulk-search" placeholder="Search asset number, name, registration…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-11 border-2" />
      </div>

      <div className="grid gap-2">
        {queue.map((a) => {
          const active = activeTagByAsset[a.id] || [];
          const verified = active.filter((t) => t.status === "active").length;
          return (
            <div key={a.id} data-testid={`bulk-asset-${a.id}`} className="bg-white border-2 border-slate-200 rounded-lg p-3 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-body font-semibold text-slate-900 truncate">{a.name}</div>
                <div className="font-mono text-xs text-slate-500">{a.asset_number}{a.registration ? ` · ${a.registration}` : ""}</div>
              </div>
              {verified > 0 && <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-bold"><CheckCircle size={16} weight="fill" />{verified} active</span>}
              {active.filter((t) => t.status !== "active").map((t) => <NfcStatusBadge key={t.id} status={t.status} />)}
              {canManage && (
                <Button data-testid={`bulk-write-${a.id}`} disabled={!supported} onClick={() => startFor(a)} className="h-10 bg-blue-600 hover:bg-blue-700 font-bold uppercase text-xs">Write Tag</Button>
              )}
            </div>
          );
        })}
        {queue.length === 0 && <p className="text-slate-400">No assets match.</p>}
      </div>

      {prog && <NfcProgrammeDialog tag={prog} open={!!prog} onClose={nextAfter} onUpdated={() => {}} onNext={nextAfter} />}
    </div>
  );
}
